import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { cn } from "./cn";

// ── Headings ────────────────────────────────────────────────────────────────
export function Display({ children, className }: { children: ReactNode; className?: string }) {
  return <h1 className={cn("display text-4xl md:text-5xl text-[var(--color-ink)]", className)}>{children}</h1>;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-8 md:mb-10 reveal reveal-1">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div className="max-w-2xl">
          {eyebrow && <div className="section-no mb-3">{eyebrow}</div>}
          <Display>{title}</Display>
          {description && <p className="mt-3 text-[15px] text-[var(--color-muted)] leading-relaxed max-w-prose">{description}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </div>
      <hr className="rule mt-6 mb-0" />
    </header>
  );
}

// ── Button ──────────────────────────────────────────────────────────────────
type ButtonVariant = "primary" | "ghost" | "danger" | "subtle" | "link";
type ButtonSize = "sm" | "md" | "lg";

const buttonStyles: Record<ButtonVariant, string> = {
  primary:
    "bg-[var(--color-ink)] text-[var(--color-paper)] hover:bg-[var(--color-ink-2)] active:translate-y-[0.5px] border border-[var(--color-ink)]",
  ghost:
    "bg-transparent text-[var(--color-ink)] hover:bg-[var(--color-paper-2)] border border-[var(--color-rule)]",
  subtle:
    "bg-[var(--color-paper-2)] text-[var(--color-ink)] hover:bg-[var(--color-rule)] border border-transparent",
  danger:
    "bg-[var(--color-rust)] text-[var(--color-paper)] hover:bg-[#852a13] border border-[var(--color-rust)]",
  link:
    "bg-transparent text-[var(--color-accent)] hover:text-[var(--color-accent-2)] underline underline-offset-[3px] decoration-[1px] border-0 p-0",
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "h-7 px-3 text-[12.5px]",
  md: "h-9 px-4 text-[13.5px]",
  lg: "h-11 px-6 text-[14.5px]",
};

export function Button({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; size?: ButtonSize }) {
  return (
    <button
      {...rest}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-sm)] font-medium transition-[background-color,border-color,transform,color] duration-150 ease-out",
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:translate-y-0",
        "whitespace-nowrap",
        variant !== "link" && sizeStyles[size],
        buttonStyles[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

// ── Inputs ──────────────────────────────────────────────────────────────────
const fieldBase =
  "block w-full bg-[var(--color-surface)] border border-[var(--color-rule)] text-[var(--color-ink)] " +
  "placeholder:text-[var(--color-mute-2)] rounded-[var(--radius-sm)] " +
  "px-3 py-[8px] text-[14px] leading-[1.45] " +
  "transition-[border-color,box-shadow] duration-150 " +
  "focus:outline-none focus:border-[var(--color-ink)] focus:shadow-[0_0_0_3px_rgba(12,13,14,0.06)] " +
  "disabled:bg-[var(--color-paper-2)] disabled:text-[var(--color-muted)]";

export function Label({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn("label-tracked block mb-2", className)}>{children}</span>;
}

export function Help({ children, className }: { children: ReactNode; className?: string }) {
  return <small className={cn("block mt-1.5 text-[12px] text-[var(--color-muted)]", className)}>{children}</small>;
}

export function Field({
  label,
  help,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; help?: ReactNode }) {
  return (
    <label className={cn("block mb-4", className)}>
      {label && <Label>{label}</Label>}
      <input {...rest} className={cn(fieldBase, "h-9", rest.className as string)} />
      {help && <Help>{help}</Help>}
    </label>
  );
}

export function Textarea({
  label,
  help,
  className,
  ...rest
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; help?: ReactNode }) {
  return (
    <label className={cn("block mb-4", className)}>
      {label && <Label>{label}</Label>}
      <textarea {...rest} className={cn(fieldBase, "min-h-[88px] resize-y font-mono text-[13px]", rest.className as string)} />
      {help && <Help>{help}</Help>}
    </label>
  );
}

export function Select({
  label,
  help,
  children,
  className,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { label?: ReactNode; help?: ReactNode }) {
  return (
    <label className={cn("block mb-4", className)}>
      {label && <Label>{label}</Label>}
      <div className="relative">
        <select
          {...rest}
          className={cn(
            fieldBase,
            "h-9 pr-9 appearance-none cursor-pointer",
            rest.className as string,
          )}
        >
          {children}
        </select>
        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-muted)]" width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </div>
      {help && <Help>{help}</Help>}
    </label>
  );
}

export function Checkbox({
  label,
  help,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; help?: ReactNode }) {
  return (
    <label className={cn("flex items-start gap-2.5 mb-3 cursor-pointer select-none", className)}>
      <span className="relative shrink-0 inline-flex h-4 w-4 items-center justify-center mt-[2px]">
        <input type="checkbox" {...rest} className="peer sr-only" />
        <span className="block h-4 w-4 rounded-[3px] border border-[var(--color-rule-strong)] bg-[var(--color-surface)] peer-checked:bg-[var(--color-ink)] peer-checked:border-[var(--color-ink)] peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-accent)] peer-focus-visible:ring-offset-2 transition-colors" />
        <svg className="absolute opacity-0 peer-checked:opacity-100 transition-opacity text-[var(--color-paper)]" width="10" height="8" viewBox="0 0 10 8" fill="none">
          <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <span>
        <span className="text-[13.5px] text-[var(--color-ink)]">{label}</span>
        {help && <span className="block text-[12px] text-[var(--color-muted)] mt-0.5">{help}</span>}
      </span>
    </label>
  );
}

// ── Surfaces ────────────────────────────────────────────────────────────────
export function Card({
  children,
  title,
  description,
  number,
  actions,
  className,
  bodyClassName,
}: {
  children?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  number?: string;
  actions?: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cn(
        "bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-md)]",
        "shadow-[var(--shadow-xs)]",
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex items-center justify-between gap-4 px-5 pt-4 pb-3 border-b border-[var(--color-rule)]">
          <div>
            {number && <div className="section-no mb-1">{number}</div>}
            {title && <h2 className="text-[15px] font-medium text-[var(--color-ink)] tracking-[-0.005em]">{title}</h2>}
            {description && <p className="text-[12.5px] text-[var(--color-muted)] mt-0.5">{description}</p>}
          </div>
          {actions && <div className="flex items-center gap-1.5 shrink-0">{actions}</div>}
        </header>
      )}
      <div className={cn("p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

export function Well({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("bg-[var(--color-paper-2)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] p-4 text-[13px] text-[var(--color-ink-2)]", className)}>
      {children}
    </div>
  );
}

// ── Status ──────────────────────────────────────────────────────────────────
type BannerKind = "info" | "error" | "success" | "warning";
const bannerStyles: Record<BannerKind, string> = {
  info: "border-[var(--color-blue)]/30 text-[#0f1f4f] bg-[#eef2ff]",
  success: "border-[var(--color-leaf)]/30 text-[var(--color-leaf)] bg-[#e9efe9]",
  error: "border-[var(--color-rust)]/30 text-[var(--color-rust)] bg-[#fbe7df]",
  warning: "border-[var(--color-amber)]/30 text-[#7a4d10] bg-[#fbf3df]",
};

export function Banner({ kind = "info", children, className, title }: { kind?: BannerKind; children: ReactNode; className?: string; title?: ReactNode }) {
  return (
    <div className={cn("rounded-[var(--radius-sm)] border px-4 py-3 text-[13.5px] mb-4", bannerStyles[kind], className)}>
      {title && <div className="font-medium mb-0.5">{title}</div>}
      {children}
    </div>
  );
}

type BadgeTone = "neutral" | "accent" | "success" | "warning" | "error" | "info";
const badgeStyles: Record<BadgeTone, string> = {
  neutral: "bg-[var(--color-paper-2)] text-[var(--color-ink-2)] border-[var(--color-rule)]",
  accent: "bg-[var(--color-accent)]/10 text-[var(--color-accent-2)] border-[var(--color-accent)]/30",
  success: "bg-[#e6ede6] text-[var(--color-leaf)] border-[var(--color-leaf)]/30",
  warning: "bg-[#fbf3df] text-[#7a4d10] border-[var(--color-amber)]/30",
  error: "bg-[#fbe7df] text-[var(--color-rust)] border-[var(--color-rust)]/30",
  info: "bg-[#eef2ff] text-[#1e3a8a] border-[var(--color-blue)]/30",
};

export function Badge({
  tone = "neutral",
  children,
  className,
}: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-[2px] text-[10.5px] font-medium leading-none uppercase tracking-[0.06em] font-mono",
        badgeStyles[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── Layout helpers ──────────────────────────────────────────────────────────
export function PageBody({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn("max-w-6xl mx-auto px-8 md:px-12 pt-10 pb-24 reveal reveal-2", className)}>{children}</div>;
}

export function StackedList({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ul className={cn("divide-y divide-[var(--color-rule)]", className)}>{children}</ul>
  );
}

export function ListItem({ children, className, onClick, href, as = "div" }: { children: ReactNode; className?: string; onClick?: () => void; href?: string; as?: "div" | "li" }) {
  const Tag = as as keyof JSX.IntrinsicElements;
  const Body = (
    <Tag
      className={cn(
        "group flex items-center justify-between gap-4 py-3.5",
        (onClick || href) && "cursor-pointer hover:text-[var(--color-accent)] transition-colors",
        className,
      )}
      onClick={onClick}
    >
      {children}
    </Tag>
  );
  return Body;
}

export function EmptyState({ title, description, icon, action, className }: { title: ReactNode; description?: ReactNode; icon?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn("py-12 px-6 text-center bg-hatch rounded-[var(--radius-sm)] border border-dashed border-[var(--color-rule-strong)]", className)}>
      {icon && <div className="mb-4 text-[var(--color-muted)] flex justify-center">{icon}</div>}
      <h3 className="display text-2xl text-[var(--color-ink)] mb-2">{title}</h3>
      {description && <p className="text-[13.5px] text-[var(--color-muted)] max-w-md mx-auto">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ── Mono / value display ───────────────────────────────────────────────────
export function Mono({ children, className }: { children: ReactNode; className?: string }) {
  return <code className={cn("font-mono text-[12.5px] text-[var(--color-ink-2)]", className)}>{children}</code>;
}

export function KeyValue({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-2 items-baseline">
      <dt className="label-tracked">{label}</dt>
      <dd className="text-[13.5px] text-[var(--color-ink)]">{children}</dd>
    </div>
  );
}

// ── Toolbar ────────────────────────────────────────────────────────────────
export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 flex-wrap", className)}>{children}</div>
  );
}

// ── Divider with text ──────────────────────────────────────────────────────
export function SectionRule({ label }: { label?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 my-6">
      <div className="h-px flex-1 bg-[var(--color-rule)]" />
      {label && <span className="label-tracked">{label}</span>}
      {label && <div className="h-px flex-1 bg-[var(--color-rule)]" />}
    </div>
  );
}
